import { useState, useEffect } from "react";
import { Sparkles, Send, X, Loader2, ShieldCheck, BookOpen, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { qstAssistantAsk } from "./api";

/* Context-aware QST Knowledge Assistant.
 * Every page passes a `context` prop describing where the user is —
 * the assistant reads that context to preseed the trade filter and offer
 * page-specific quick prompts. Answers stay strictly grounded.
 *
 * context = {
 *   page: "home" | "trade" | "lesson" | "library" | "tools" | "calculator"
 *          | "for-orgs" | "pricing" | "contact" | "story",
 *   trade?: string,       // trade slug when on a trade or lesson page
 *   lessonTitle?: string, // when on a lesson page
 *   category?: string,    // when a filter is active
 *   toolLabel?: string,   // when on a tool page
 * }
 */

/* Page-specific quick prompts + greeting the assistant will show. */
const CONTEXT_PRESETS = {
  home:     { greeting: "How can I help you find training today?", quick: [
    "How do I verify materials before installation?",
    "What's the daily trench safety inspection?",
    "How do I run a concrete pour QC check?",
  ]},
  trade:    { greeting: (ctx) => `You're browsing ${ctx.tradeLabel || "this trade"}. Ask me anything from its lessons.`, quick: (ctx) => [
    `Show me a quick ${ctx.tradeLabel || 'trade'} safety talk`,
    `What are common quality issues in ${ctx.tradeLabel || 'this trade'}?`,
    `How do I document a ${ctx.tradeLabel || 'trade'} training session?`,
  ]},
  lesson:   { greeting: (ctx) => `Ask questions about "${ctx.lessonTitle}" — I'll answer from the same source only.`, quick: (ctx) => [
    "Summarize this lesson in 3 sentences",
    "What discussion questions should I ask?",
    "What are the biggest root causes?",
  ]},
  library:  { greeting: "Search grounded to any lesson in the library. Try a real jobsite question.", quick: [
    "How do I do a pressure test on plumbing?",
    "What's the arc-flash procedure?",
    "How do we secure a load on a truck?",
  ]},
  tools:    { greeting: "Pick a calculator or tool, or ask which one fits your situation.", quick: [
    "How much does rework typically cost?",
    "What's the real cost of an injury?",
    "How do I build a toolbox talk from scratch?",
  ]},
  calculator: { greeting: (ctx) => `${ctx.toolLabel || 'Calculator'} tips — ask what to include or how to interpret results.`, quick: (ctx) => [
    "What indirect costs am I forgetting?",
    "How do industry averages compare?",
    "How do I export this to my project team?",
  ]},
  "for-orgs": { greeting: "How does QST fit your organization? Ask about programs, reporting or bulk pricing.", quick: [
    "How does QST work for a GC with 30 subcontractors?",
    "Can insurers get aggregate reporting?",
    "What does an OCIP program get?",
  ]},
  pricing:  { greeting: "Not sure which plan fits? Tell me about your team and I'll recommend one.", quick: [
    "I'm one foreman with a small crew — which plan?",
    "We're a 200-worker contractor across 5 projects",
    "We're an insurer covering 40 policyholders",
  ]},
  contact:  { greeting: "Want a quick answer before you email us? Ask here first.", quick: [
    "Do you support Spanish?",
    "How long does setup take?",
    "Do you offer OCIP / CCIP programs?",
  ]},
  story:    { greeting: "About to submit a story? I can help sanity-check what to include or leave out.", quick: [
    "Should I include the company name?",
    "How do you anonymize the story?",
    "What makes a story publishable?",
  ]},
  dashboard: { greeting: "You're on the contractor dashboard. Ask about assignments, overdue lessons, or reports.", quick: [
    "Which crews are overdue?",
    "How do I add a new project?",
    "How do I export a training report?",
  ]},
};

function resolveContext(ctx = {}) {
  const preset = CONTEXT_PRESETS[ctx.page] || CONTEXT_PRESETS.home;
  const greeting = typeof preset.greeting === "function" ? preset.greeting(ctx) : preset.greeting;
  const quick = typeof preset.quick === "function" ? preset.quick(ctx) : preset.quick;
  return { greeting, quick };
}

export default function QstAssistantWidget({ variant = "docked", context = { page: "home" } }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [trade, setTrade] = useState(context.trade || "");
  const [busy, setBusy] = useState(false);
  const [reply, setReply] = useState(null);
  const { greeting, quick } = resolveContext(context);

  useEffect(() => { setTrade(context.trade || ""); }, [context.trade]);

  async function ask(overrideQ) {
    const question = overrideQ || q;
    if (!question || question.length < 3) return;
    setBusy(true); setReply(null);
    try {
      const r = await qstAssistantAsk({
        question,
        trade: trade || undefined,
        category: context.category || undefined,
      });
      setReply(r);
    } catch (e) {
      setReply({ grounded: false, answer: "The assistant is temporarily unavailable. Please try again in a moment.", sources: [] });
    } finally {
      setBusy(false);
    }
  }

  if (variant === "docked") {
    return (
      <>
        <button onClick={() => setOpen(true)}
          className="fixed right-4 md:right-6 top-1/2 -translate-y-1/2 z-30 bg-gradient-to-b from-[#0B2545] to-[#1D4ED8] text-white shadow-2xl rounded-l-xl px-2 py-4 hover:pl-3 transition"
          data-testid="qst-assistant-open">
          <div className="flex flex-col items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#F97316]" />
            <div className="text-[10px] font-bold tracking-[0.2em] [writing-mode:vertical-rl]">
              QST AI ASSIST
            </div>
          </div>
        </button>
        {open && (
          <AssistantPanel
            greeting={greeting} quick={quick}
            q={q} setQ={setQ} trade={trade} setTrade={setTrade}
            busy={busy} reply={reply} ask={ask} close={() => setOpen(false)}
          />
        )}
      </>
    );
  }
  return null;
}

function AssistantPanel({ greeting, quick, q, setQ, trade, setTrade, busy, reply, ask, close }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-900/40 backdrop-blur-sm"
         onClick={close} data-testid="qst-assistant-panel">
      <div onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-xl bg-[#FFF7ED] sm:rounded-2xl shadow-2xl max-h-[92vh] overflow-y-auto border border-orange-200">
        <div className="sticky top-0 bg-gradient-to-r from-[#F97316] via-[#EA580C] to-[#0B2545] text-white px-5 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-white/25 backdrop-blur grid place-items-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="text-sm font-bold">QST Knowledge Assistant</div>
              <div className="text-[11px] text-white/85">Grounded in expert-reviewed lessons only</div>
            </div>
          </div>
          <button onClick={close} className="p-1.5 hover:bg-white/15 rounded-md" data-testid="qst-assistant-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5">
          <div className="text-sm text-slate-800 mb-4 font-semibold">{greeting}</div>

          {quick && quick.length > 0 && (
            <div className="mb-4">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">Try a quick question</div>
              <div className="flex flex-wrap gap-1.5">
                {quick.map((p, i) => (
                  <button key={i} onClick={() => { setQ(p); ask(p); }}
                    className="rounded-full bg-white hover:bg-orange-50 text-slate-800 text-xs font-semibold px-3 py-1.5 border border-orange-200 hover:border-[#F97316]"
                    data-testid={`qst-assistant-quick-${i}`}>
                    {p}
                  </button>
                ))}
              </div>
            </div>
          )}

          <label className="block text-xs font-bold uppercase tracking-widest text-slate-600 mb-2">
            What are you trying to do?
          </label>
          <textarea rows={3} value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="e.g. How do I verify materials before installation?"
            className="w-full rounded-lg border-2 border-orange-200 bg-white text-slate-900 px-3 py-2.5 text-sm focus:border-[#F97316] focus:ring-2 focus:ring-orange-200 outline-none"
            data-testid="qst-assistant-input" />
          <label className="block mt-3 text-xs font-bold uppercase tracking-widest text-slate-600 mb-2">
            Trade (optional)
          </label>
          <select value={trade} onChange={(e) => setTrade(e.target.value)}
            className="w-full rounded-lg border-2 border-orange-200 bg-white text-slate-900 px-3 py-2.5 text-sm focus:border-[#F97316] outline-none"
            data-testid="qst-assistant-trade">
            <option value="">Any trade</option>
            <option value="electrical-commercial">Electrical — Commercial</option>
            <option value="electrical-low-voltage">Electrical — Low Voltage</option>
            <option value="concrete-rebar">Concrete &amp; Rebar</option>
            <option value="plumbing">Plumbing</option>
            <option value="mechanical">Mechanical</option>
            <option value="roofing-waterproofing">Roofing &amp; Waterproofing</option>
            <option value="excavation">Excavation</option>
            <option value="finishes">Finishes</option>
            <option value="doors-windows-siding">Doors, Windows &amp; Siding</option>
            <option value="sheet-metal">Sheet Metal</option>
            <option value="fire-protection">Fire Protection</option>
            <option value="alternative-energy">Alternative Energy</option>
          </select>
          <button onClick={() => ask()} disabled={busy || !q}
            className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#F97316] hover:bg-[#EA580C] disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold py-3 rounded-lg transition shadow-md"
            data-testid="qst-assistant-submit">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Searching approved lessons…</> :
              <><Send className="w-4 h-4" /> Ask QST</>}
          </button>

          <div className="mt-3 flex items-start gap-2 text-[11px] text-slate-600">
            <ShieldCheck className="w-3.5 h-3.5 mt-0.5 text-[#059669] flex-shrink-0" />
            The QST assistant only summarizes expert-approved content. It refuses to answer if the library has no matching lesson.
          </div>

          {reply && (
            <div className="mt-5 rounded-lg border-2 border-orange-100 bg-white p-4 shadow-sm" data-testid="qst-assistant-reply">
              <div className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${reply.grounded ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-900"}`}>
                {reply.grounded ? "Grounded answer" : "No approved match"}
              </div>
              <pre className="mt-2 whitespace-pre-wrap text-sm text-slate-800 font-sans leading-relaxed">{reply.answer}</pre>
              {reply.sources && reply.sources.length > 0 && (
                <div className="mt-4">
                  <div className="text-[11px] font-bold uppercase tracking-widest text-slate-600 mb-2">
                    Approved sources
                  </div>
                  <ul className="space-y-1.5">
                    {reply.sources.map((s) => (
                      <li key={s.slug} className="flex items-start gap-2 text-sm">
                        <BookOpen className="w-3.5 h-3.5 mt-1 text-[#F97316] flex-shrink-0" />
                        <Link to={`/qst/lessons/${s.slug}`} onClick={close}
                          className="text-slate-800 hover:text-[#0B2545] font-semibold hover:underline">
                          {s.title}
                          <span className="text-[10px] text-slate-500 ml-1.5">
                            v{s.version} · reviewed {s.last_reviewed}
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {reply.disclaimer && (
                <p className="mt-4 text-[11px] text-slate-500 italic">{reply.disclaimer}</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
